// Die Datenqualitaetsfaelle in der Oberflaeche (ST-TAX-002, Folgeschnitt B10;
// ADR-0059): meine offenen Faelle und das Schliessen -- nicht mehr (D5).
//
// FACHSPRACHE: "Stichwortkonflikte", "Stichwortliste", "Fall", "behoben", "ist so gewollt" -- keine
// Regelcodes, keine Kennungen (NFR-UX-003). Ein Satz je Grundcode mit Problem,
// Wirkung und naechstem Schritt (NFR-UX-005).

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Faelle je Antwort -- Abschrift von MAXIMUM_LISTED_ISSUES, die Probe liest sie dort. */
export const MAXIMUM_SHOWN_ISSUES = 25;

export interface DataQualityIssueView {
  readonly dataQualityIssueId: string;
  readonly partyId: string;
  readonly partyDisplayName: string;
  readonly taxonomyDisplayName: string;
  readonly detectedAt: string;
  readonly fromBatch: boolean;
  readonly openTermCount: number;
}

export type IssuesResult =
  | { readonly outcome: 'loaded'; readonly issues: readonly DataQualityIssueView[]; readonly hasMore: boolean }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type CloseResult = { readonly outcome: 'closed' } | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export const NOT_OPEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Fall ist schon geschlossen.',
  explanation: 'Er wurde inzwischen als behoben oder als gewollt markiert — vielleicht von dir in einem anderen Fenster.',
  nextStep: 'Lade die Liste neu.',
  retryable: true,
});

export const GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diesen Fall gibt es für dich nicht mehr.',
  explanation: 'Er gehört einer anderen Person, oder du darfst den Kontakt nicht mehr sehen. Es wurde nichts geändert.',
  nextStep: 'Lade die Liste neu.',
  retryable: true,
});

/** Laden ohne Antwort: geschrieben ist dabei nichts. */
export const UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Deine Fälle ließen sich nicht laden.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

/** Schliessen ohne Antwort: ob es gewirkt hat, zeigt erst der neue Stand. */
export const CLOSE_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob der Fall geschlossen wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lade die Seite neu. Dann siehst du, was gilt.',
  retryable: true,
});

export const CONFLICT_PERSISTS_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Kontakt trägt noch mehr als ein Stichwort dieser Stichwortliste.',
  explanation: 'Behoben ist der Fall erst, wenn nur noch eines übrig ist. Es wurde nichts geändert.',
  nextStep: 'Entferne am Kontakt, was nicht passt, oder schließe den Fall als gewollt.',
  retryable: false,
});

const REASON_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  data_quality_issue_not_open: NOT_OPEN_MESSAGE,
  data_quality_issue_conflict_persists: CONFLICT_PERSISTS_MESSAGE,
  data_quality_issue_not_found: GONE_MESSAGE,
});

export const EXPLAINED_REASON_CODES: readonly string[] = Object.freeze(Object.keys(REASON_MESSAGES));

function messageFor(reasonCode: string | undefined, facts: ResponseFacts): PlainMessage {
  return (reasonCode === undefined ? undefined : REASON_MESSAGES[reasonCode]) ?? plainMessageFor(reasonCode, facts);
}

/** Was der Fall ist, in einem Satz -- und ob er sich inzwischen erledigt hat. */
export function issueSentence(issue: Pick<DataQualityIssueView, 'taxonomyDisplayName' | 'openTermCount'>): string {
  if (issue.openTermCount <= 1) {
    return `Die Stichwortliste „${issue.taxonomyDisplayName}“ sieht ein Stichwort vor, und inzwischen trägt der Kontakt nur noch ${issue.openTermCount === 1 ? 'eines' : 'keines'}. Du kannst den Fall als behoben schließen.`;
  }
  return `Die Stichwortliste „${issue.taxonomyDisplayName}“ sieht ein Stichwort vor, der Kontakt trägt ${issue.openTermCount}. Entferne am Kontakt, was nicht passt, oder schließe den Fall, wenn es so gewollt ist.`;
}

export function issuesSummary(count: number, hasMore: boolean): string {
  if (count === 0) return 'Du hast keine offenen Stichwortkonflikte.';
  const gezaehlt = count === 1 ? 'Ein offener Fall' : `${count} offene Fälle`;
  return hasMore
    ? `${gezaehlt} — die ältesten ${MAXIMUM_SHOWN_ISSUES}. Es gibt noch weitere; sie erscheinen, sobald du einige schließt.`
    : gezaehlt;
}

/**
 * "Behoben" passt nur, wenn der Konflikt aufgeloest ist (Experience-Review
 * 28.09.2026, Befund 2). Solange der Kontakt mehr als ein Stichwort der
 * Stichwortliste traegt, ist die Aktion gesperrt und dieser Satz sagt warum.
 * Nur Darstellung: der Server nimmt beide Ausgaenge weiter an (D4).
 */
export function resolveBlockedReason(openTermCount: number): string | undefined {
  return openTermCount > 1 ? 'Behoben geht erst, wenn der Kontakt nur noch ein Stichwort dieser Stichwortliste trägt.' : undefined;
}

/**
 * Der Satz nach einem Massenlauf. Die Faelle gehoeren der URHEBERIN des Laufs
 * (D1) -- wer als zweite Person freigibt, bekommt einen Satz ohne "fuer dich"
 * (Codex-Cloud-Review #274, P2).
 */
export function createdIssuesSentence(count: number, forMe = true): string | undefined {
  if (count <= 0) return undefined;
  if (!forMe) {
    return count === 1
      ? 'Ein Kontakt trägt jetzt zwei Stichworte derselben Stichwortliste. Er steht bei der Person, die den Massenlauf angelegt hat, unter „Stichwortkonflikte“.'
      : `${count} Kontakte tragen jetzt zwei Stichworte derselben Stichwortliste. Sie stehen bei der Person, die den Massenlauf angelegt hat, unter „Stichwortkonflikte“.`;
  }
  return count === 1
    ? 'Ein Kontakt trägt jetzt zwei Stichworte derselben Stichwortliste. Er steht für dich unter „Stichwortkonflikte“.'
    : `${count} Kontakte tragen jetzt zwei Stichworte derselben Stichwortliste. Sie stehen für dich unter „Stichwortkonflikte“.`;
}

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

const UNVOLLSTAENDIG: PlainMessage = Object.freeze({
  headline: 'Die Stichwortkonflikte lassen sich nicht anzeigen.',
  explanation: 'LIAISON hat geantwortet, aber die Antwort war unvollständig.',
  nextStep: 'Lade die Seite neu. Bleibt es dabei, wende dich an die LIAISON-Administration.',
  retryable: true,
});

function viewOf(value: unknown): DataQualityIssueView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.dataQualityIssueId !== 'string' || typeof row.partyId !== 'string' || typeof row.partyDisplayName !== 'string' ||
    typeof row.taxonomyDisplayName !== 'string' || typeof row.detectedAt !== 'string' || typeof row.fromBatch !== 'boolean' ||
    typeof row.openTermCount !== 'number' || !Number.isInteger(row.openTermCount) || row.openTermCount < 0
  ) {
    return undefined;
  }
  return {
    dataQualityIssueId: row.dataQualityIssueId,
    partyId: row.partyId,
    partyDisplayName: row.partyDisplayName,
    taxonomyDisplayName: row.taxonomyDisplayName,
    detectedAt: row.detectedAt,
    fromBatch: row.fromBatch,
    openTermCount: row.openTermCount,
  };
}

export async function fetchOwnIssues(options: { readonly baseUrl: string; readonly accessToken: string; readonly send?: SendLike }): Promise<IssuesResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/data-quality-issues`, { headers: { authorization: `Bearer ${options.accessToken}` } });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: messageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const views = Array.isArray(body.issues) ? body.issues.map(viewOf) : [undefined];
  if (views.includes(undefined)) {
    return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  }
  return { outcome: 'loaded', issues: views as DataQualityIssueView[], hasMore: body.hasMore === true };
}

export async function closeIssue(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly dataQualityIssueId: string;
  readonly outcome: 'resolve' | 'dismiss';
  readonly send?: SendLike;
}): Promise<CloseResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/data-quality-issues/${encodeURIComponent(options.dataQualityIssueId)}/${options.outcome}`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: '{}',
  });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: messageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  return { outcome: 'closed' };
}
